import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Star, Plus, Trash2, Play, Film, Share2, Copy, Check, X, Mail, MessageSquare } from "lucide-react";
import ClipPlayer from "@/components/ClipPlayer";

const HighlightsPage = () => {
  const { api } = useAuth();
  const [highlights, setHighlights] = useState([]);
  const [shareHighlight, setShareHighlight] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [clips, setClips] = useState([]);
  const [selectedClips, setSelectedClips] = useState([]);
  const [activeTape, setActiveTape] = useState(null);
  const [loadingTape, setLoadingTape] = useState(false);

  useEffect(() => {
    api().get("/highlights").then(r => setHighlights(r.data)).catch(() => {});
    api().get("/clips/my").then(r => setClips(r.data)).catch(() => {});
  }, [api]);

  const createHighlight = async () => {
    if (!title || selectedClips.length === 0) return;
    const r = await api().post("/highlights", { title, description: desc, clip_ids: selectedClips });
    setHighlights(prev => [r.data, ...prev]);
    setShowForm(false);
    setTitle(""); setDesc(""); setSelectedClips([]);
  };

  const deleteHighlight = async (id) => {
    await api().delete(`/highlights/${id}`);
    setHighlights(prev => prev.filter(h => h.id !== id));
  };

  const playHighlight = async (h) => {
    setLoadingTape(true);
    try {
      const r = await api().get(`/highlights/${h.id}/clips`);
      setActiveTape({ highlight: r.data.highlight, clips: r.data.clips });
    } catch (err) {
      alert("Failed to load highlight clips");
    } finally {
      setLoadingTape(false);
    }
  };

  const toggleClip = (id) => {
    setSelectedClips(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const moveClip = (from, dir) => {
    const to = from + dir;
    if (to < 0 || to >= selectedClips.length) return;
    const next = [...selectedClips];
    [next[from], next[to]] = [next[to], next[from]];
    setSelectedClips(next);
  };

  if (activeTape) {
    return (
      <ClipPlayer
        clips={activeTape.clips}
        title={activeTape.highlight?.title}
        subtitle={`${activeTape.clips.length} clips`}
        onBack={() => setActiveTape(null)}
      />
    );
  }

  return (
    <div data-testid="highlights-page" style={{ padding: '32px 40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 700 }}>Highlights</h1>
        <button data-testid="create-highlight-btn" onClick={() => setShowForm(!showForm)} style={{
          background: 'var(--cyan)', color: '#000', border: 'none', cursor: 'pointer',
          padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 6,
        }}><Plus size={16} /> Create</button>
      </div>

      {showForm && (
        <div data-testid="highlight-form" className="glass-card" style={{ padding: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>New Highlight Tape</h3>
          <input className="input-field" data-testid="highlight-title" placeholder="Highlight title" value={title} onChange={e => setTitle(e.target.value)} style={{ marginBottom: 12 }} />
          <textarea className="input-field" data-testid="highlight-desc" placeholder="Description" value={desc} onChange={e => setDesc(e.target.value)} rows={2} style={{ marginBottom: 16, resize: 'vertical' }} />

          {clips.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '12px 0' }}>No clips yet — create some first from the Games page.</p>
          ) : (
            <>
              {/* Ordered selection */}
              {selectedClips.length > 0 && (
                <div data-testid="highlight-selected-clips" style={{ marginBottom: 16, background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 10, padding: 12 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>In tape ({selectedClips.length})</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {selectedClips.map((cid, idx) => {
                      const c = clips.find(x => x.id === cid);
                      if (!c) return null;
                      return (
                        <div key={cid} data-testid={`selected-clip-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#1a2230', border: '1px solid #253040', borderRadius: 8, padding: '8px 10px' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cyan)', minWidth: 18 }}>{idx + 1}</span>
                          <span style={{ flex: 1, fontSize: 13, color: '#fff' }}>{c.title}</span>
                          <span style={{ fontSize: 11, color: '#888' }}>{(c.end_time - c.start_time).toFixed(1)}s</span>
                          <button data-testid={`move-up-${idx}`} disabled={idx === 0} onClick={() => moveClip(idx, -1)} style={reorderBtnStyle}>↑</button>
                          <button data-testid={`move-down-${idx}`} disabled={idx === selectedClips.length - 1} onClick={() => moveClip(idx, +1)} style={reorderBtnStyle}>↓</button>
                          <button data-testid={`remove-clip-${idx}`} onClick={() => toggleClip(cid)} style={{ ...reorderBtnStyle, color: '#f44336' }}>×</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Available clips */}
              <p style={{ fontSize: 12, fontWeight: 700, color: '#888', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Available clips</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
                {clips.filter(c => !selectedClips.includes(c.id)).map(c => (
                  <button key={c.id} type="button" data-testid={`add-clip-${c.id}`} onClick={() => toggleClip(c.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, background: '#0f141d', border: '1px solid #253040',
                    borderRadius: 8, padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
                  }}>
                    <Plus size={14} style={{ color: 'var(--cyan)' }} />
                    <span style={{ flex: 1, fontSize: 13, color: '#fff' }}>{c.title}</span>
                    <span style={{ fontSize: 11, color: '#888' }}>{c.game_title}</span>
                    <span style={{ fontSize: 11, color: '#888' }}>{(c.end_time - c.start_time).toFixed(1)}s</span>
                  </button>
                ))}
                {clips.filter(c => !selectedClips.includes(c.id)).length === 0 && (
                  <p style={{ fontSize: 12, color: '#666', padding: '8px 0' }}>All your clips are in the tape.</p>
                )}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { setShowForm(false); setSelectedClips([]); setTitle(''); setDesc(''); }} style={{ padding: '10px 18px', background: 'transparent', border: '1px solid #253040', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={createHighlight} data-testid="save-highlight-btn" disabled={!title || selectedClips.length === 0} className="btn-primary" style={{ opacity: (!title || selectedClips.length === 0) ? 0.5 : 1, cursor: (!title || selectedClips.length === 0) ? 'not-allowed' : 'pointer' }}>
              Create Highlight Tape ({selectedClips.length} clip{selectedClips.length === 1 ? '' : 's'})
            </button>
          </div>
        </div>
      )}

      {highlights.length === 0 && !showForm ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Star size={56} style={{ color: 'var(--text-secondary)', opacity: 0.3, marginBottom: 16 }} />
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No Highlight Tapes</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Create a highlight tape from your best clips</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {highlights.map(h => (
            <div key={h.id} data-testid={`highlight-card-${h.id}`} className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
              <button
                type="button"
                data-testid={`play-highlight-${h.id}`}
                onClick={() => playHighlight(h)}
                disabled={loadingTape || (h.clip_ids?.length || 0) === 0}
                style={{
                  width: '100%', padding: '28px 20px', background: 'linear-gradient(135deg, #00e5ff 0%, #0097a7 100%)',
                  border: 'none', cursor: (h.clip_ids?.length || 0) === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                }}
              >
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {loadingTape ? <Film size={24} style={{ color: '#0a1628' }} /> : <Play size={24} fill="#0a1628" style={{ color: '#0a1628', marginLeft: 3 }} />}
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#0a1628', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Play Highlight Tape</span>
              </button>
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <Star size={18} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                  <h3 style={{ fontSize: 16, fontWeight: 600 }}>{h.title}</h3>
                </div>
                {h.description && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6 }}>{h.description}</p>}
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>{h.clip_ids?.length || 0} clip{(h.clip_ids?.length || 0) === 1 ? '' : 's'} &bull; by {h.created_by_name}</p>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button onClick={() => setShareHighlight(h)} data-testid={`share-highlight-${h.id}`} style={{ background: 'none', border: 'none', color: 'var(--cyan)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                    <Share2 size={12} /> Share
                  </button>
                  <button onClick={() => deleteHighlight(h.id)} data-testid={`delete-highlight-${h.id}`} style={{ background: 'none', border: 'none', color: '#f44336', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {shareHighlight && (
        <ShareHighlightModal highlight={shareHighlight} onClose={() => setShareHighlight(null)} />
      )}
    </div>
  );
};

export default HighlightsPage;

const reorderBtnStyle = {
  width: 26, height: 26, borderRadius: 6, background: '#253040',
  border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

/* ═══ Share Highlight Modal — native share + social link buttons + copy link ═══ */
const ShareHighlightModal = ({ highlight, onClose }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/share/highlight/${highlight.id}`;
  const shareText = `Check out "${highlight.title}" on YSV`;

  const doNativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: highlight.title, text: shareText, url: shareUrl }); onClose(); }
      catch {}
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const encU = encodeURIComponent(shareUrl);
  const encT = encodeURIComponent(shareText);
  const socials = [
    { key: "twitter", label: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${encU}&text=${encT}`, color: "#fff" },
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encU}`, color: "#1877f2" },
    { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encU}`, color: "#0a66c2" },
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${encT}%20${encU}`, color: "#25d366" },
  ];

  return (
    <div data-testid="share-highlight-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 24, width: 'min(96vw, 460px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', gap: 8 }}>
            <Share2 size={18} style={{ color: 'var(--cyan)' }} /> Share this highlight
          </h3>
          <button onClick={onClose} data-testid="share-modal-close" style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 18 }}>
          Anyone with this link can watch <strong style={{ color: '#fff' }}>"{highlight.title}"</strong> — no login required.
        </p>

        {/* Link row */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input data-testid="share-highlight-url" readOnly value={shareUrl} style={{
            flex: 1, background: '#0c0c10', border: '1px solid var(--dark-border)', borderRadius: 10,
            color: '#fff', padding: '10px 12px', fontSize: 13, fontFamily: 'monospace',
          }} onClick={e => e.target.select()} />
          <button data-testid="share-highlight-copy" onClick={copy} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
        </div>

        {typeof navigator !== "undefined" && navigator.share && (
          <button data-testid="share-highlight-native" onClick={doNativeShare} style={{
            width: '100%', padding: '12px', background: 'rgba(0,229,255,0.12)',
            border: '1px solid rgba(0,229,255,0.3)', borderRadius: 10,
            color: 'var(--cyan)', cursor: 'pointer', fontSize: 14, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginBottom: 14, fontFamily: "'DM Sans', sans-serif",
          }}>
            <Share2 size={16} /> Open device share sheet
          </button>
        )}

        {/* Socials grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10 }}>
          {socials.map(s => (
            <a key={s.key} data-testid={`share-highlight-${s.key}`} href={s.href} target="_blank" rel="noopener noreferrer" style={{
              padding: '10px 12px', background: '#0c0c10', border: '1px solid var(--dark-border)',
              borderRadius: 10, color: s.color, fontSize: 13, fontWeight: 600,
              textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              <Share2 size={12} /> {s.label}
            </a>
          ))}
          <a data-testid="share-highlight-email" href={`mailto:?subject=${encT}&body=${encT}%0A%0A${encU}`} style={{
            padding: '10px 12px', background: '#0c0c10', border: '1px solid var(--dark-border)',
            borderRadius: 10, color: '#ff6b00', fontSize: 13, fontWeight: 600,
            textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <Mail size={12} /> Email
          </a>
          <a data-testid="share-highlight-sms" href={`sms:?&body=${encT}%20${encU}`} style={{
            padding: '10px 12px', background: '#0c0c10', border: '1px solid var(--dark-border)',
            borderRadius: 10, color: '#4caf50', fontSize: 13, fontWeight: 600,
            textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <MessageSquare size={12} /> Text / SMS
          </a>
        </div>
      </div>
    </div>
  );
};

